// ---------------------------------------------------------------------------
// Renderer: World Map
//
// Equirectangular world map with active-country shading, facility markers,
// and a footer legend.  GeoJSON data is loaded asynchronously and cached.
// Polygon rendering is batched into two Konva.Shape nodes for performance.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { addDebugOverlay } from "./helpers.js";

// ── Geo data ─────────────────────────────────────────────────────────────

let _worldGeoJSON = null;
let _worldLoadPromise = null;

function loadWorldGeoJSON() {
  if (_worldGeoJSON) return Promise.resolve(_worldGeoJSON);
  if (_worldLoadPromise) return _worldLoadPromise;
  _worldLoadPromise = fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson")
    .then(r => r.json())
    .then(geojson => { _worldGeoJSON = geojson; return _worldGeoJSON; });
  return _worldLoadPromise;
}

const CITY_COORDS = {
  "london": [51.5074, -0.1278], "new york": [40.7128, -74.0060],
  "paris": [48.8566, 2.3522], "tokyo": [35.6762, 139.6503],
  "singapore": [1.3521, 103.8198], "sydney": [-33.8688, 151.2093],
  "mumbai": [19.0760, 72.8777], "sao paulo": [-23.5505, -46.6333],
  "mexico city": [19.4326, -99.1332], "nairobi": [-1.2921, 36.8219],
  "cape town": [-33.9249, 18.4241], "lagos": [6.5244, 3.3792],
  "dubai": [25.2048, 55.2708], "shanghai": [31.2304, 121.4737],
  "berlin": [52.5200, 13.4050], "madrid": [40.4168, -3.7038],
  "amsterdam": [52.3676, 4.9041], "dublin": [53.3498, -6.2603],
  "edinburgh": [55.9533, -3.1883], "glasgow": [55.8642, -4.2518],
  "melbourne": [-37.8136, 144.9631]
};

function geocodeCity(name) {
  return CITY_COORDS[(name || "").toLowerCase().trim()] || null;
}

function project(lng, lat, mapW, mapH) {
  return [((lng + 180) / 360) * mapW, ((90 - lat) / 180) * mapH];
}

let _mapDataPromise = null;

export function renderMap(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 600, height = 400, imageCallback, theme, fontScale = 1 } = options;
  const s = fontScale;
  const group = new Konva.Group({ x, y, width, height, __async: true });
  const mapData = crystalizedData[block.id] || {};

  const doRender = (geoJSON) => {
    group.destroyChildren();
    if (!geoJSON) {
      group.add(new CustomText({
        x: 0, y: height / 2 - Math.round(10 * s), width,
        text: "Loading map…", fontSize: Math.round(12 * s), fill: "#999", align: "center",
        refreshCallback: imageCallback
      }));
      return;
    }

    const availW = width - Math.round(16 * s);
    const availH = height - Math.round(16 * s);
    let mapW, mapH;
    if (availW / Math.max(availH, 1) > 2) { mapH = availH; mapW = mapH * 2; }
    else { mapW = availW; mapH = mapW / 2; }
    const ox = Math.round(8 * s + (availW - mapW) / 2);
    const oy = Math.round(8 * s + (availH - mapH) / 2);

    group.add(new Konva.Rect({
      x: Math.round(4 * s), y: Math.round(4 * s),
      width: width - Math.round(8 * s), height: height - Math.round(8 * s),
      fill: "#E5E0D2", cornerRadius: Math.round(4 * s)
    }));

    const activeCountries = new Set((mapData.active_countries || []).map(c => (c || "").toLowerCase().trim()));
    const activeFill = (block.shading && block.shading.active) || "#1F3A2E";
    const activePaths = [];
    const inactivePaths = [];

    for (const f of (geoJSON.features || [])) {
      const countryName = ((f.properties && (f.properties.name || f.properties.NAME)) || "").toLowerCase().trim();
      if (!countryName || countryName === "antarctica") continue;
      const isActive = activeCountries.has(countryName);
      const geom = f.geometry;
      if (!geom) continue;
      const target = isActive ? activePaths : inactivePaths;

      const collectRings = (coords) => {
        const rings = Array.isArray(coords[0][0]) ? coords : [coords];
        for (const ring of rings) {
          const lngs = ring.map(p => p[0]);
          if (Math.max(...lngs) - Math.min(...lngs) > 300) continue;
          const pts = [];
          for (const [lng, lat] of ring) {
            const [px, py] = project(lng, lat, mapW, mapH);
            pts.push(px + ox, py + oy);
          }
          if (pts.length > 4) target.push(pts);
        }
      };
      if (geom.type === "Polygon") collectRings(geom.coordinates);
      else if (geom.type === "MultiPolygon") for (const poly of geom.coordinates) collectRings(poly);
    }

    if (activePaths.length > 0) {
      group.add(new Konva.Shape({
        x: 0, y: 0, width, height,
        _mapPolygons: activePaths.map(pts => ({ pts, fill: activeFill, fillOpacity: 0.85, stroke: activeFill, strokeWidth: 0.5 })),
        sceneFunc(ctx, shape) {
          for (const p of shape.attrs._mapPolygons) {
            ctx.beginPath(); ctx.moveTo(p.pts[0], p.pts[1]);
            for (let i = 2; i < p.pts.length; i += 2) ctx.lineTo(p.pts[i], p.pts[i + 1]);
            ctx.closePath(); ctx.fillStyle = p.fill; ctx.globalAlpha = p.fillOpacity; ctx.fill();
            ctx.lineWidth = p.strokeWidth; ctx.strokeStyle = p.stroke; ctx.stroke();
          }
        }, listening: false, perfectDrawEnabled: false,
      }));
    }
    if (inactivePaths.length > 0) {
      group.add(new Konva.Shape({
        x: 0, y: 0, width, height,
        _mapPolygons: inactivePaths.map(pts => ({ pts, fill: "#EDE8DD", fillOpacity: 1, stroke: "#9E9A8E", strokeWidth: 1 })),
        sceneFunc(ctx, shape) {
          for (const p of shape.attrs._mapPolygons) {
            ctx.beginPath(); ctx.moveTo(p.pts[0], p.pts[1]);
            for (let i = 2; i < p.pts.length; i += 2) ctx.lineTo(p.pts[i], p.pts[i + 1]);
            ctx.closePath(); ctx.fillStyle = p.fill; ctx.globalAlpha = p.fillOpacity; ctx.fill();
            ctx.lineWidth = p.strokeWidth; ctx.strokeStyle = p.stroke; ctx.stroke();
          }
        }, listening: false, perfectDrawEnabled: false,
      }));
    }

    for (const city of (mapData.cities || [])) {
      let lat, lng;
      if (typeof city.lat === "number" && typeof city.lng === "number") { lat = city.lat; lng = city.lng; }
      else { const coords = geocodeCity(city.name); if (!coords) continue; lat = coords[0]; lng = coords[1]; }
      const [cx, cy] = project(lng, lat, mapW, mapH);
      const dotR = Math.round(3 * s);
      group.add(new Konva.Circle({ x: cx + ox, y: cy + oy, radius: dotR + Math.round(1 * s), fill: "#fff", opacity: 0.85 }));
      group.add(new Konva.Circle({ x: cx + ox, y: cy + oy, radius: dotR, fill: city.color || "#C4463A" }));
      if (city.label) group.add(new CustomText({
        x: cx + ox + Math.round(6 * s), y: cy + oy - Math.round(6 * s),
        text: city.label, fontSize: Math.round(9 * s), fill: "#333", refreshCallback: imageCallback
      }));
    }

    if (mapData.country_count != null || mapData.site_count != null) {
      group.add(new CustomText({
        x: ox, y: oy + mapH - Math.round(18 * s), width: mapW,
        text: `${mapData.country_count || 0} countries · ${mapData.site_count || 0} sites`,
        fontSize: Math.round(10 * s), fill: "#555", align: "center", refreshCallback: imageCallback
      }));
    }
    addDebugOverlay(group, block, { ...options, height });
  };

  if (_worldGeoJSON) { doRender(_worldGeoJSON); }
  else {
    doRender(null);
    if (!_mapDataPromise) _mapDataPromise = loadWorldGeoJSON();
    _mapDataPromise.then(gj => { _worldGeoJSON = gj; doRender(gj); if (imageCallback) imageCallback(group); });
  }

  return group;
}
